import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './SuggestionOverrides.module.scss';

const splitLines = value => String(value || '')
  .split('\n')
  .map(item => item.trim())
  .filter(Boolean);

const ChannelEditor = ({ channelKey, value, onChange }) => {
  const t = useLocalizedMessage();
  const prefix = `suggestionOverrides.channel.${channelKey}`;
  return (
    <section className={styles.channel} data-testid={`suggestion-channel-${channelKey}`}>
      <div className={styles.channelHeading}>
        <div>
          <Typography variant="subtitle1">{t(`${prefix}.title`)}</Typography>
          <Typography variant="body2" color="textSecondary">{t(`${prefix}.description`)}</Typography>
        </div>
        <FormControl variant="outlined" size="small" className={styles.modeControl}>
          <InputLabel id={`${channelKey}-mode-label`}>{t('suggestionOverrides.mode.label')}</InputLabel>
          <Select
            labelId={`${channelKey}-mode-label`}
            label={t('suggestionOverrides.mode.label')}
            value={value.mode}
            onChange={event => onChange({ ...value, mode: event.target.value })}
            inputProps={{ 'data-testid': `${channelKey}-mode` }}
          >
            <MenuItem value="prepend">{t('suggestionOverrides.mode.prepend')}</MenuItem>
            <MenuItem value="replace">{t('suggestionOverrides.mode.replace')}</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className={styles.channelGrid}>
        <TextField
          label={t(`${prefix}.values`)}
          helperText={t('suggestionOverrides.listHint')}
          value={value.values.join('\n')}
          onChange={event => onChange({ ...value, values: splitLines(event.target.value) })}
          multiline
          minRows={4}
          variant="outlined"
          inputProps={{ 'data-testid': `${channelKey}-values` }}
        />
        <TextField
          label={t(`${prefix}.excluded`)}
          helperText={t('suggestionOverrides.listHint')}
          value={value.excluded.join('\n')}
          onChange={event => onChange({ ...value, excluded: splitLines(event.target.value) })}
          multiline
          minRows={4}
          variant="outlined"
          inputProps={{ 'data-testid': `${channelKey}-excluded` }}
        />
      </div>
    </section>
  );
};

ChannelEditor.propTypes = {
  channelKey: PropTypes.oneOf(['terms', 'words']).isRequired,
  value: PropTypes.shape({
    mode: PropTypes.oneOf(['prepend', 'replace']).isRequired,
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
    excluded: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

const SuggestionOverrideRuleCard = ({ rule, onUpdate, onDelete }) => {
  const t = useLocalizedMessage();
  const updateChannel = (channel, value) => onUpdate(rule._id, { [channel]: value });
  return (
    <Paper
      variant="outlined"
      className={styles.ruleCard}
      data-testid={`suggestion-override-rule-${rule._id}`}
    >
      <div className={styles.ruleHeader}>
        <TextField
          label={t('suggestionOverrides.rule.query')}
          helperText={t('suggestionOverrides.rule.queryHint')}
          value={rule.query}
          onChange={event => onUpdate(rule._id, { query: event.target.value })}
          variant="outlined"
          size="small"
          className={styles.queryControl}
          inputProps={{
            'aria-label': t('suggestionOverrides.rule.query'),
            'data-testid': 'suggestion-override-query',
          }}
        />
        <TextField
          label={t('suggestionOverrides.rule.language')}
          helperText={t('suggestionOverrides.rule.languageHint')}
          value={rule.language_id}
          onChange={event => onUpdate(rule._id, { language_id: event.target.value.toLowerCase() })}
          variant="outlined"
          size="small"
          className={styles.languageControl}
          inputProps={{
            'aria-label': t('suggestionOverrides.rule.language'),
            'data-testid': 'suggestion-override-language',
          }}
        />
        <FormControlLabel
          className={styles.enabledControl}
          control={(
            <Switch
              checked={rule.enabled}
              onChange={event => onUpdate(rule._id, { enabled: event.target.checked })}
              color="primary"
              inputProps={{ 'aria-label': t('suggestionOverrides.rule.enabled') }}
            />
          )}
          label={t('suggestionOverrides.rule.enabled')}
        />
        <Tooltip title={t('button.delete')}>
          <IconButton
            onClick={() => onDelete(rule._id)}
            aria-label={t('button.delete')}
            data-testid="suggestion-override-delete"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className={styles.channels}>
        <ChannelEditor
          channelKey="terms"
          value={rule.terms}
          onChange={value => updateChannel('terms', value)}
        />
        <ChannelEditor
          channelKey="words"
          value={rule.words}
          onChange={value => updateChannel('words', value)}
        />
      </div>
    </Paper>
  );
};

SuggestionOverrideRuleCard.propTypes = {
  rule: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    query: PropTypes.string.isRequired,
    language_id: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    terms: PropTypes.object.isRequired,
    words: PropTypes.object.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default SuggestionOverrideRuleCard;
